import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionHeading from "../../components/SectionHeading";
import { useNavigate } from "react-router-dom";
import FrequentlyUsedViews from "./FrequentlyUsedViews";
import Loader from "../../components/ui/Loader";
import { useSelector, useDispatch } from "react-redux";
import { useLazyGetReportDetailsQuery } from "../../slices/saveReportViewSlice";
import { useLazyGetPortalUserInfoQuery } from "../../slices/dataviewApiSlice";
import { useFlags } from "launchdarkly-react-client-sdk";
import GTM from "../../GTM";
import {
    filterDocumentDataViews,
    filterSelectedReport,
    getHighVolumeReport,
    getIsDocumentLibraryTileEnabled,
    getSelectedIsfOrganizationIds,
    getSubViewNames,
    getIsOldChangesTileEnabled,
    getIsNewLeaveChangesEnabled,
    filterChangesDataViews,
    getDocumentLibraryPermission,
} from "../../utils/helpers";
import {
    changesReportType,
    DATA_VIEW_TYPES,
    DATAVIEWTYPE,
    lifeType,
    REPORT_ITEMS,
} from "../../constants";
import {
    setPortalUserData,
    setReportDetails,
    setSelectedDataViewType,
    setSelectedReportViewId,
} from "../../slices/viewReportSlice";
import RequestFailed from "../../components/alerts/RequestFailed";
import PropTypes from "prop-types";
import RichText from "../../components/RichText";

const ReportCard = React.memo(
    ({
        title,
        icon,
        path,
        text,
        announcement,
        notes,
        displayCards,
        reportData,
    }) => {
        const gtm = new GTM(null);
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const { user } = useSelector((state) => state.auth);
        const {
            displayDataViewTypes,
            displayDataViewTypesExternal,
            displayLifeForUsers,
            enableHighVolumeExport,
        } = useFlags();

        const {
            enableExternalUser,
            enableInternalUser,
            enableOrganizationList,
            isfUserIds,
            subViewNames,
        } = enableHighVolumeExport;

        const isHighVolumeReport = getHighVolumeReport({
            enableExternalUser,
            enableInternalUser,
            enableOrganizationList,
            isfUserIds,
            subViewNames,
            user,
        });

        const dataViewTypes = displayDataViewTypes.split(",");
        const dataViewTypesExternal = displayDataViewTypesExternal.split(",");
        const enabledUsersForLife = displayLifeForUsers?.split(",");

        const handleClick = () => {
            navigate(`/reportingcapabilities/${path}`);
            dispatch(setSelectedDataViewType(path));

            if (!isHighVolumeReport) {
                const selectedReportDetails = reportData.find((obj) => {
                    if (obj.DataViewType.includes(path.slice(0, 4))) {
                        return obj.Name;
                    }
                });

                dispatch(
                    setSelectedReportViewId(selectedReportDetails?.ViewId)
                );
            }

            gtm.Event(
                "run reports",
                "button",
                "click",
                title.toLowerCase(),
                "reports",
                "",
                "report_card_click",
                ""
            );
        };

        const handleKeyDown = (event) => {
            if (event.key === "Enter" || event.keyCode === 13) {
                navigate(`/reportingcapabilities/${path}`);
                dispatch(setSelectedDataViewType(path));
            }
        };

        const displayLifeCard = () => {
            return (
                (enabledUsersForLife.includes("All") ||
                    enabledUsersForLife?.includes(
                        user?.userId?.toLowerCase()
                    )) &&
                path === lifeType
            );
        };

        const isCardEnabled =
            (dataViewTypes.includes(path) && user?.isUserInternal) ||
            (dataViewTypesExternal.includes(path) && !user?.isUserInternal) ||
            displayLifeCard();

        const disabledStyle = {
            pointerEvents: "none",
            opacity: 0.4,
            cursor: "not-allowed",
        };

        const style = displayCards && !isCardEnabled ? disabledStyle : {};

        const buttonLabel = title.toLowerCase();

        return (
            <button
                className="section-card mb-0p report-card"
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                tabIndex={displayCards ? 0 : -1}
                disabled={!displayCards}
                aria-disabled={!displayCards}
                style={style}
            >
                <div className="card-wrapper">
                    <div style={{ flex: 1 }}>
                        <div
                            className="d-flex"
                            style={{ gap: 8, alignItems: "center" }}
                        >
                            <div className={`${buttonLabel} icon`}>
                                <img src={icon} alt={buttonLabel} />
                            </div>

                            <SectionHeading heading={title} mb="mb-0p" />
                        </div>

                        {announcement?.length > 0 && (
                            <RichText segments={announcement} />
                        )}

                        <p
                            style={{
                                textAlign: "left",
                                marginBottom: 0,
                                marginTop: 16,
                            }}
                            className="card-content"
                        >
                            {text}
                        </p>

                        {notes?.length > 0 && <RichText segments={notes} />}
                    </div>

                    <span className="a-self" style={{ padding: "7px 0" }}>
                        <div className="d-flex button">
                            <span>View {buttonLabel}</span>
                            <i className="fa-solid fa-chevron-right"></i>
                        </div>
                    </span>
                </div>
            </button>
        );
    }
);

ReportCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    announcement: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            bold: PropTypes.bool,
        })
    ),
    displayCards: PropTypes.bool.isRequired,
    reportData: PropTypes.array.isRequired,
};

const ReportViews = ({ displayReportView, organizations, user }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        displaySubViewReports,
        displayDocumentLibraryTile,
        displayoldChangesTile,
        displayNewLeaveChangesReport,
    } = useFlags();

    const {
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
    } = displaySubViewReports || {};

    const allOrganizations = useSelector(
        (state) => state?.userDetails?.organizations
    );

    const isDocumentLibraryTileEnabled = getIsDocumentLibraryTileEnabled({
        displayDocumentLibraryTile,
        user,
        allOrganizations,
    });

    const isOldChangesTileEnabled = getIsOldChangesTileEnabled({
        displayoldChangesTile,
        user,
        allOrganizations,
    });

    const isNewLeaveChangesEnabled = getIsNewLeaveChangesEnabled({
        displayNewLeaveChangesReport,
        user,
        allOrganizations,
    });

    const visibleReportItems = REPORT_ITEMS.filter((item) => {
        if (item.path === changesReportType) return isOldChangesTileEnabled;
        if (item.path === DATA_VIEW_TYPES.DOCUMENTS)
            return isDocumentLibraryTileEnabled;
        return true;
    });

    const [reportData, setReportData] = useState([]);

    const frequentlyUsedData = filterChangesDataViews(
        reportData,
        isOldChangesTileEnabled,
        isNewLeaveChangesEnabled
    );

    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports: selectedRelatedOrganizations,
        portalUserData: storedPortalUserData,
    } = useSelector((state) => state.reportOrganization);

    const { totalParentOrganizations } = useSelector(
        (state) => state.organizations
    );
    const { selectedIsfOrganizationIds, organizationGroupId } =
        getSelectedIsfOrganizationIds(
            userSelectedOrganizationReports,
            reportId,
            selectedRelatedOrganizations,
            totalParentOrganizations
        );

    const { dlselectedIsfOrganizationId, dlViewId, dlDataView } = useSelector(
        (state) => state.auth
    );

    const [
        getPortalUser,
        {
            data: portalUserData,
            isError,
            isFetching: isFetchingUserData,
            isUninitialized,
        },
    ] = useLazyGetPortalUserInfoQuery();

    useEffect(() => {
        if (userSelectedOrganizationReports && portalUserData) {
            dispatch(setPortalUserData(portalUserData?.userPermissions));
        }
    }, [portalUserData, dispatch]);

    const hasDocumentLibraryPermission = useMemo(() => {
        if (isUninitialized || !displayReportView) return true;

        return getDocumentLibraryPermission(portalUserData?.userPermissions);
    }, [portalUserData, isUninitialized, displayReportView]);

    const [
        getReportDetails,
        { data: reportDetailsData, isFetching, isError: isReportDetailsError },
    ] = useLazyGetReportDetailsQuery();

    useEffect(() => {
        if (reportDetailsData) {
            const updatedSelectedReport = filterDocumentDataViews(
                filterSelectedReport(
                    reportDetailsData,
                    subViewNames,
                    filterResult
                ),
                isDocumentLibraryTileEnabled
            );
            setReportData(updatedSelectedReport);
            dispatch(setReportDetails(updatedSelectedReport));
        }
    }, [reportDetailsData, isDocumentLibraryTileEnabled]);

    const filterResult = getSubViewNames({
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
        user,
    });

    const reportDetailsParams = {
        isfUserId: user.userId,
        dataViewType: DATAVIEWTYPE,
        selectedIsfOrganizationIds: selectedIsfOrganizationIds,
        organizationGroupId: organizationGroupId
            ? organizationGroupId
            : undefined,
    };

    const relatedOrganizationIDISF = useMemo(
        () =>
            userSelectedOrganizationReports[reportId] &&
            userSelectedOrganizationReports[reportId]?.related?.map(
                (item) => item.ORGANIZATIONIDISF
            ),
        [userSelectedOrganizationReports, reportId]
    );

    const organizationIDISF = useMemo(
        () =>
            userSelectedOrganizationReports[reportId] && [
                userSelectedOrganizationReports[reportId]?.organization
                    ?.ORGANIZATIONIDISF,
            ],
        [userSelectedOrganizationReports, reportId]
    );

    const orgsToCompare = useMemo(
        () =>
            userSelectedOrganizationReports[reportId]?.related?.length > 0
                ? relatedOrganizationIDISF
                : organizationIDISF,
        [
            organizationIDISF,
            relatedOrganizationIDISF,
            userSelectedOrganizationReports,
            reportId,
        ]
    );

    const sortedOrgsToCompare = useMemo(
        () => orgsToCompare?.map((org) => org?.toLowerCase())?.sort(),
        [orgsToCompare]
    );

    const shouldCallPortalUserApi = useCallback(
        (userData) => {
            const assignedOrgs = userData?.assignedOrgs?.map((obj) =>
                obj.orgId.toLowerCase()
            );

            const sortedAssignedOrgs = assignedOrgs?.sort();

            return !(
                sortedAssignedOrgs?.length === sortedOrgsToCompare?.length &&
                sortedAssignedOrgs?.every(
                    (value, index) => value === sortedOrgsToCompare[index]
                )
            );
        },
        [sortedOrgsToCompare]
    );

    useEffect(() => {
        if (
            userSelectedOrganizationReports[reportId] &&
            shouldCallPortalUserApi(storedPortalUserData)
        ) {
            const isfUserId = String(user.userId).toUpperCase();
            const organizationId =
                userSelectedOrganizationReports[reportId]?.related?.length > 0
                    ? relatedOrganizationIDISF
                    : organizationIDISF;

            getPortalUser({
                isfUserId,
                organizationId,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userSelectedOrganizationReports,
        storedPortalUserData,
        shouldCallPortalUserApi,
        reportId,
    ]);

    useEffect(() => {
        if (displayReportView || organizations.length === 1) {
            getReportDetails(reportDetailsParams);
        }
    }, [
        displayReportView,
        organizations.length,
        JSON.stringify(selectedIsfOrganizationIds),
    ]);

    useEffect(() => {
        if (
            Array.isArray(reportData) &&
            reportData.length > 0 &&
            dlselectedIsfOrganizationId &&
            dlViewId &&
            dlDataView
        ) {
            navigate(`/reportingcapabilities/${dlDataView}`);
            dispatch(setSelectedDataViewType(dlDataView));
            dispatch(setSelectedReportViewId(Number(dlViewId)));
        }
    }, [
        reportData,
        dlselectedIsfOrganizationId,
        dlViewId,
        dlDataView,
        navigate,
        dispatch,
        portalUserData,
        isError,
    ]);

    const isLoading = isFetching || isFetchingUserData;

    return (
        <div>
            {isReportDetailsError ? (
                <RequestFailed />
            ) : (
                <div role="status">
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div
                            className="report-views"
                            style={
                                displayReportView
                                    ? {}
                                    : {
                                          pointerEvents: "none",
                                          opacity: "0.4",
                                          gridColumn: 1 / 3,
                                          gridRow: 1 / 4,
                                      }
                            }
                        >
                            {visibleReportItems.map((item, index) => {
                                if (
                                    item.path === DATA_VIEW_TYPES.DOCUMENTS &&
                                    !hasDocumentLibraryPermission
                                ) {
                                    return null;
                                }

                                // When the last row holds a single card, let it span the full width
                                const isLastCardAlone =
                                    visibleReportItems.length % 3 === 1 &&
                                    index === visibleReportItems.length - 1;

                                return (
                                    <div
                                        key={item.path}
                                        className={
                                            isLastCardAlone
                                                ? "report-card-full-width"
                                                : ""
                                        }
                                    >
                                        <ReportCard
                                            key={item.path}
                                            {...item}
                                            reportData={reportData}
                                            displayCards={displayReportView}
                                            isError={isError}
                                        />
                                    </div>
                                );
                            })}

                            <FrequentlyUsedViews
                                data={frequentlyUsedData}
                                isFetching={isFetching}
                                isError={isError}
                                portalUserData={portalUserData?.userPermissions}
                                displayReportView={displayReportView}
                            />
                        </div>
                    )}
                </div>
            )}{" "}
        </div>
    );
};

ReportViews.propTypes = {
    displayReportView: PropTypes.bool.isRequired,
    organizations: PropTypes.array.isRequired,
    user: PropTypes.shape({
        userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isUserInternal: PropTypes.bool,
    }).isRequired,
};

export default ReportViews;
